import * as Sentry from '@sentry/browser';
import type {AgentDeclaration, AgentPage, AgentRowType} from './agentDeclaration';
import {canSeeAgent, setDeployedAgentIds} from './agentVisibility';
import {isRecord, validateAgentDeclaration} from './validateAgentDeclaration';

const UNKNOWN_AGENT_ID = 'unknown';

// Real agents are added here; the next agent needs only its declaration and one line.
const PRODUCTION_AGENTS: AgentDeclaration[] = [];

const declarations: AgentDeclaration[] = [];

let hasRegisteredKnownAgents = false;

const readAgentId = (candidate: unknown): string => {
	const agentId = isRecord(candidate) ? candidate.id : undefined;
	return typeof agentId === 'string' && agentId.trim().length > 0 ? agentId : UNKNOWN_AGENT_ID;
};

// Refusing must never throw: registration runs when the toolbar module loads, so a throw would break the page.
const refuse = (message: string): false => {
	// eslint-disable-next-line no-console
	console.warn(message);
	Sentry.captureMessage(message, 'warning');
	return false;
};

export const registerAgent = (candidate: AgentDeclaration): boolean => {
	const agentId = readAgentId(candidate);
	const slotGaps = validateAgentDeclaration(candidate);
	if (slotGaps.length > 0) {
		return refuse(`Agent declaration "${agentId}" refused. Missing or invalid slots: ${slotGaps.join(', ')}`);
	}
	if (declarations.some(declaration => declaration.id === agentId)) {
		return refuse(`Agent declaration "${agentId}" refused. An agent with this id is already registered.`);
	}
	declarations.push(candidate);
	return true;
};

export const ensureAgentsRegistered = (): void => {
	if (hasRegisteredKnownAgents) {
		return;
	}
	hasRegisteredKnownAgents = true;
	PRODUCTION_AGENTS.forEach(registerAgent);
	// The stub agents are required inside this literal NODE_ENV check, not imported, so production
	// builds drop the branch and never bundle them.
	if (process.env.NODE_ENV === 'development') {
		// eslint-disable-next-line @typescript-eslint/no-var-requires
		const {LOCAL_STUB_AGENTS}: {LOCAL_STUB_AGENTS: AgentDeclaration[]} = require('./testing/stubAgents');
		LOCAL_STUB_AGENTS.forEach(registerAgent);
		setDeployedAgentIds(LOCAL_STUB_AGENTS.map(declaration => declaration.id));
	}
};

export const getAgentsForPage = (page: AgentPage): AgentDeclaration[] =>
	declarations.filter(declaration => declaration.pages.includes(page));

export const getVisibleAgentsForRow = (page: AgentPage, rowType: AgentRowType): AgentDeclaration[] =>
	getAgentsForPage(page).filter(declaration => declaration.rows.includes(rowType) && canSeeAgent(declaration));

export const clearAgentRegistry = (): void => {
	declarations.length = 0;
	hasRegisteredKnownAgents = false;
	setDeployedAgentIds([]);
};
